import { useQuery } from '@tanstack/react-query';

import { apiFetch } from '../api/client';

/**
 * Phase 2 — React Query hook for /api/deals/field-options.
 *
 * Spec: docs/archive/pre-cut-2026-08/phase-b-deals-card-fields-spec.md §7.1.
 *
 * Single hook fetches the option set for every ENUM field the Deals
 * card renders. The result is cached with staleTime=1h since admins
 * change vocab values infrequently; the A1 admin-edit mutations
 * invalidate this query key on save so the cache busts immediately
 * after a vocab edit.
 */

export interface DealsFieldOption {
  value: string;
  label: string;
  description: string | null;
  active: boolean;
}

export interface DealsFieldOptionsResponse {
  fields: Record<string, DealsFieldOption[]>;
}

/** Fixed list of ENUM fields the Deals card needs options for. Pass-through
 * to the backend's `field_names=` query param. Stays in sync with the
 * `dropdown_single` + `dropdown_multi` entries in FIELD_CONTROLS. */
export const DROPDOWN_FIELD_NAMES = [
  'proposed_stage',
  'deal_type',
  'vertical',
  'themes',
  'sourcing_type',
  'sourcing_channel',
] as const;

export function useDealsFieldOptions() {
  return useQuery<DealsFieldOptionsResponse>({
    queryKey: ['deals', 'field-options'],
    queryFn: () =>
      apiFetch<DealsFieldOptionsResponse>(
        `/api/deals/field-options?field_names=${DROPDOWN_FIELD_NAMES.join(',')}`,
      ),
    // Vocab options change infrequently. 1hr stale time is generous.
    // A1 admin edits invalidate this key on save (see useVocabularies.ts
    // mutations) so changes propagate immediately when made via the UI.
    staleTime: 60 * 60 * 1000,
  });
}

/** Helper for components that need just one field's options. */
export function useFieldOptions(fieldName: string): {
  options: DealsFieldOption[];
  isLoading: boolean;
  error: Error | null;
} {
  const q = useDealsFieldOptions();
  return {
    options: q.data?.fields[fieldName] ?? [],
    isLoading: q.isLoading,
    error: q.error,
  };
}
